import {
  AgentDelegationEndedReason,
  AgentDelegationStatus,
  DelegationBlockedReason,
  ExecutorAttention,
  ExecutorAttentionReason,
  type InboxEntry,
  type InboxEntryAgentDelegation,
} from '@this+that/models';

import { buildRunKey, toServedList, withAgentRuns } from './listTicketRuns';

export interface RunStatusSignals {
  hasLedgerPendingApproval: boolean;
  hasOpenQuestion: boolean;
  isRunActivelyWorking: boolean;
  isLedgerLoaded: boolean;
  /** The ledger's latest decided proposal was declined and nothing ran after it. */
  isLedgerDeclined?: boolean;
}

export interface PresentedRunStatus {
  status: AgentDelegationStatus;
  blockedReason: DelegationBlockedReason | null;
  isApprovalPending: boolean;
  clearsPending: boolean;
  isDeclined: boolean;
}

export interface PresentedStatusOverride {
  startedAt: number | null;
  fromStatus: AgentDelegationStatus;
  status: AgentDelegationStatus;
  blockedReason: DelegationBlockedReason | null;
  clearsPending: boolean;
  endedReason: AgentDelegationEndedReason | null;
  /** The ledger shows no pending proposal at all, so the entry's NEEDS_APPROVAL attention is stale. */
  isApprovalAttentionDisproved: boolean;
}

const SUPERSEDED_BY_ACTIVE_RUN = new Set<AgentDelegationStatus>([
  AgentDelegationStatus.QUEUED,
  AgentDelegationStatus.DISPATCHING,
  AgentDelegationStatus.BLOCKED,
]);

export function isStoredApprovalBlock(
  delegation: Pick<
    InboxEntryAgentDelegation,
    'blockedReason' | 'pendingApproval' | 'pendingQuestion'
  >,
): boolean {
  return (
    delegation.blockedReason === DelegationBlockedReason.NEEDS_APPROVAL ||
    delegation.pendingApproval != null ||
    delegation.pendingQuestion?.isApproval === true
  );
}

function isWaitingForReplyBlock(
  delegation: InboxEntryAgentDelegation,
): boolean {
  return (
    delegation.status === AgentDelegationStatus.BLOCKED &&
    delegation.blockedReason === DelegationBlockedReason.WAITING_REPLY
  );
}

function unchanged(
  delegation: InboxEntryAgentDelegation,
  isApprovalPending: boolean,
): PresentedRunStatus {
  return {
    status: delegation.status,
    blockedReason: delegation.blockedReason ?? null,
    isApprovalPending,
    clearsPending: false,
    isDeclined: false,
  };
}

function isStoredDeclined(delegation: InboxEntryAgentDelegation): boolean {
  return (
    delegation.status === AgentDelegationStatus.DONE &&
    delegation.endedReason === AgentDelegationEndedReason.DECLINED_BY_OWNER
  );
}

function resolveDoneUnchanged(
  delegation: InboxEntryAgentDelegation,
  signals: RunStatusSignals,
): PresentedRunStatus {
  return {
    ...unchanged(delegation, false),
    isDeclined:
      isStoredDeclined(delegation) || signals.isLedgerDeclined === true,
  };
}

/**
 * The one place the run card, the header chip and the board banner get their status from.
 * Precedence: an in-flight step beats Queued/Blocked; a question beats an approval it is not;
 * a stored approval block the executor ledger no longer backs (the run already printed its
 * terminal line) settles to DONE instead of waiting on the owner forever.
 */
export function resolvePresentedRunStatus(
  delegation: InboxEntryAgentDelegation,
  signals: RunStatusSignals,
): PresentedRunStatus {
  const { status } = delegation;
  if (isWaitingForReplyBlock(delegation)) {
    return unchanged(delegation, false);
  }
  if (signals.isRunActivelyWorking && SUPERSEDED_BY_ACTIVE_RUN.has(status)) {
    return {
      ...unchanged(delegation, false),
      status: AgentDelegationStatus.RUNNING,
      blockedReason: null,
      clearsPending: true,
    };
  }
  const storedApproval =
    !signals.isLedgerLoaded &&
    status === AgentDelegationStatus.BLOCKED &&
    isStoredApprovalBlock(delegation);
  const isApprovalPending = signals.hasLedgerPendingApproval || storedApproval;
  if (isApprovalPending) {
    return unchanged(delegation, true);
  }
  if (signals.hasOpenQuestion) {
    const isFailed = status === AgentDelegationStatus.FAILED;
    return {
      status: isFailed ? AgentDelegationStatus.BLOCKED : status,
      blockedReason:
        status === AgentDelegationStatus.BLOCKED || isFailed
          ? DelegationBlockedReason.NEEDS_INPUT
          : (delegation.blockedReason ?? null),
      isApprovalPending: false,
      clearsPending: false,
      isDeclined: false,
    };
  }
  if (
    signals.isLedgerLoaded &&
    status === AgentDelegationStatus.BLOCKED &&
    isStoredApprovalBlock(delegation)
  ) {
    return {
      status: AgentDelegationStatus.DONE,
      blockedReason: null,
      isApprovalPending: false,
      clearsPending: true,
      isDeclined: signals.isLedgerDeclined === true,
    };
  }
  return status === AgentDelegationStatus.DONE
    ? resolveDoneUnchanged(delegation, signals)
    : unchanged(delegation, false);
}

export function resolvePresentedEndedReason(
  presented: PresentedRunStatus,
): AgentDelegationEndedReason | null {
  return presented.isDeclined
    ? AgentDelegationEndedReason.DECLINED_BY_OWNER
    : null;
}

export function buildPresentedStatusOverride(
  delegation: InboxEntryAgentDelegation,
  presented: PresentedRunStatus,
  isApprovalAttentionDisproved = false,
): PresentedStatusOverride | null {
  const endedReason = resolvePresentedEndedReason(presented);
  const changesEndedReason = endedReason !== (delegation.endedReason ?? null);
  if (
    !presented.clearsPending &&
    presented.status === delegation.status &&
    !changesEndedReason &&
    !isApprovalAttentionDisproved
  ) {
    return null;
  }
  return {
    startedAt: delegation.startedAt ?? null,
    fromStatus: delegation.status,
    status: presented.status,
    blockedReason: presented.blockedReason,
    clearsPending: presented.clearsPending,
    endedReason,
    isApprovalAttentionDisproved,
  };
}

function withoutApprovalReason(
  reasons: InboxEntry['executorAttentionReasons'],
): string[] {
  return [...(reasons ?? [])].filter(
    (reason) => reason !== ExecutorAttentionReason.NEEDS_APPROVAL,
  );
}

function stripApprovalAttention(entry: InboxEntry): InboxEntry {
  const { executorAttentionReasons, executorAttention } = entry;
  const hasApprovalReason =
    executorAttentionReasons != null &&
    [...executorAttentionReasons].includes(
      ExecutorAttentionReason.NEEDS_APPROVAL,
    );
  const hasApprovalScalar =
    executorAttention === ExecutorAttention.NEEDS_APPROVAL;
  if (!hasApprovalReason && !hasApprovalScalar) {
    return entry;
  }
  return {
    ...entry,
    ...(executorAttentionReasons != null && {
      executorAttentionReasons: withoutApprovalReason(executorAttentionReasons),
    }),
    ...(hasApprovalScalar && { executorAttention: ExecutorAttention.NONE }),
  };
}

function isOverrideCurrent(
  delegation: InboxEntryAgentDelegation,
  override: PresentedStatusOverride,
): boolean {
  return (
    delegation.status === override.fromStatus &&
    (delegation.startedAt ?? null) === override.startedAt
  );
}

function settleDelegation(
  delegation: InboxEntryAgentDelegation,
  override: PresentedStatusOverride,
): InboxEntryAgentDelegation {
  return {
    ...delegation,
    status: override.status,
    blockedReason: override.blockedReason ?? undefined,
    endedReason: override.endedReason ?? undefined,
    ...(override.clearsPending && {
      pendingApproval: undefined,
      pendingQuestion: undefined,
    }),
  };
}

/** Applies an override only while the stored delegation is still the one it was derived from. */
export function applyPresentedStatusOverride(
  entry: InboxEntry,
  override: PresentedStatusOverride | undefined,
): InboxEntry {
  const delegation = entry.agentDelegation;
  if (!override || !delegation || !isOverrideCurrent(delegation, override)) {
    return entry;
  }
  const settled: InboxEntry = {
    ...entry,
    agentDelegation: settleDelegation(delegation, override),
  };
  return override.isApprovalAttentionDisproved
    ? stripApprovalAttention(settled)
    : settled;
}

/** The same settling for every run of the ticket: the primary reads the override published
 *  under its entry id, each secondary run the one under its own run key. */
export function applyPresentedStatusOverrides(
  entry: InboxEntry,
  overrides: Readonly<Record<string, PresentedStatusOverride>>,
): InboxEntry {
  const settledPrimary = applyPresentedStatusOverride(
    entry,
    overrides[entry.id],
  );
  const secondary = toServedList(entry.agentRuns);
  if (secondary.length === 0) {
    return settledPrimary;
  }
  let hasChange = false;
  const settledRuns = secondary.map((delegation) => {
    const override = overrides[buildRunKey(entry.id, delegation.delegationId)];
    if (!override || !isOverrideCurrent(delegation, override)) {
      return delegation;
    }
    hasChange = true;
    return settleDelegation(delegation, override);
  });
  return hasChange
    ? withAgentRuns(settledPrimary, settledRuns)
    : settledPrimary;
}
